import React from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import CountUp from "react-countup";
import {
  ArrowRight, Leaf, Sprout, GraduationCap, Egg, ShoppingBasket, HeartHandshake,
  MapPin, Star, Phone, ShieldCheck, Users, TrendingDown, AlertTriangle, CheckCircle2,
  PhoneCall, BookOpenCheck, Wheat, HelpCircle,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal, StaggerChildren, staggerItem } from "../components/Reveal";
import { useLang, useLocalePath } from "../contexts/LanguageContext";
import SEO, { organizationSchema, faqSchema } from "../components/SEO";

export default function Home() {
  const { t } = useLang();
  const localePath = useLocalePath();
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 600], [0, 120]);

  const services = [
    { icon: Leaf, key: "s1", img: "/images/gallery/lsp-4.jpg" },
    { icon: Sprout, key: "s2", img: "/images/gallery/lsp-5.jpg" },
    { icon: GraduationCap, key: "s3", img: "/images/gallery/lsp-6.jpg" },
    { icon: Egg, key: "s4", img: "/images/gallery/lsp-7.jpg" },
    { icon: ShoppingBasket, key: "s5", img: "/images/gallery/lsp-8.jpg" },
    { icon: HeartHandshake, key: "s6", img: "/images/gallery/lsp-9.jpg" },
  ];

  const problems = [
    { icon: TrendingDown, key: "p1" },
    { icon: AlertTriangle, key: "p2" },
    { icon: Wheat, key: "p3" },
    { icon: Users, key: "p4" },
  ];

  const steps = [
    { icon: PhoneCall, key: "s1", n: "01" },
    { icon: BookOpenCheck, key: "s2", n: "02" },
    { icon: Sprout, key: "s3", n: "03" },
  ];

  const trustBadges = [
    { icon: ShieldCheck, key: "b1" },
    { icon: Users, key: "b2" },
    { icon: Sprout, key: "b3" },
    { icon: HeartHandshake, key: "b4" },
  ];

  const impact = [
    { n: 2000, suffix: "+", key: "i1" },
    { n: 120, suffix: "+", key: "i2" },
    { n: 45, suffix: "+", key: "i3" },
    { n: 6, suffix: "", key: "i4" },
  ];

  const testimonials = [
    { q: t("testimonials_home.t1_q"), by: t("testimonials_home.t1_by") },
    { q: t("testimonials_home.t2_q"), by: t("testimonials_home.t2_by") },
    { q: t("testimonials_home.t3_q"), by: t("testimonials_home.t3_by") },
    { q: t("testimonials_home.t4_q"), by: t("testimonials_home.t4_by") },
    { q: t("testimonials_home.t5_q"), by: t("testimonials_home.t5_by") },
  ];

  const faqs = t("faq_home.items") || [];
  const locations = ["Niali", "Cuttack", "Bhubaneswar", "Khordha", "Dhenkanal", "Puri Outskirts"];

  return (
    <div className="overflow-hidden">
      <SEO metaKey="home" pathname="/" schemas={[organizationSchema(), faqSchema(faqs)]} />

      {/* HERO */}
      <section data-testid="hero-section" className="relative isolate min-h-[92vh] lg:min-h-screen flex items-end pb-24 lg:pb-32 bg-forest overflow-hidden">
        {/* Background image with gentle parallax */}
        <motion.div style={{ y: yImg }} className="absolute inset-0 z-0 will-change-transform">
          <img src="/images/gallery/lsp-1.jpg" alt="Farmer in paddy field, Odisha" className="w-full h-[115%] object-cover" loading="eager" />
        </motion.div>
        {/* Static overlays — NOT inside parallax so they don't shift on scroll */}
        <div className="absolute inset-0 z-[1] bg-black/55 md:bg-forest/55 pointer-events-none" />
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black/30 via-transparent to-forest/45 pointer-events-none" />
        {/* Smooth bottom fade into next section — kills the harsh dark line */}
        <div className="absolute inset-x-0 bottom-0 z-[2] h-32 bg-gradient-to-b from-transparent via-[#F2EEE7]/70 to-[#F2EEE7] pointer-events-none" />

        {/* Content — NO parallax, stays anchored. Prevents disappearing on scroll. */}
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 w-full">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/95 text-[12px] tracking-[0.25em] uppercase mb-8">
              <Leaf className="w-3.5 h-3.5 text-mustard" /> {t("hero.eyebrow")}
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.35 }}
              className="font-serif font-light text-[2.75rem] sm:text-6xl lg:text-[7rem] xl:text-[8rem] leading-[0.96] text-white tracking-tight drop-shadow-[0_2px_30px_rgba(0,0,0,0.35)]">
              {t("hero.h1a")}
              <span className="block italic text-mustard">{t("hero.h1b")}</span>
              <span className="block text-white/95">{t("hero.h1c")}</span>
            </motion.h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-8 max-w-2xl text-white/90 text-base lg:text-xl leading-relaxed">
              {t("hero.sub_before")} <b className="text-mustard font-medium">{t("hero.sub_bold")}</b> {t("hero.sub_after")}
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-10 flex flex-wrap gap-3 sm:gap-4">
              <a href="tel:+919938056120" data-testid="hero-call-btn" className="btn-pill bg-mustard text-forest hover:bg-mustard/90">
                <Phone className="w-4 h-4" /> {t("hero.cta_call")}
              </a>
              <Link to={localePath("/training")} data-testid="hero-training-btn" className="btn-pill btn-terra">
                {t("hero.cta_training")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to={localePath("/services")} data-testid="hero-explore-btn" className="btn-pill bg-white/10 backdrop-blur-md text-white border border-white/30 hover:bg-white/20">
                {t("hero.cta_work")}
              </Link>
            </motion.div>

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.1 }}
              className="mt-6 text-white/85 text-[13px] flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-mustard shrink-0" /> {t("hero.tagline")}
            </motion.p>
          </div>
        </div>
      </section>

      {/* TRUST BADGES */}
      <section data-testid="trust-badges-section" className="border-y border-dust bg-kraft/40">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-10 lg:py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
            {trustBadges.map((b, i) => (
              <Reveal key={b.key} delay={i * 0.08}>
                <div data-testid={`trust-badge-${i}`} className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-forest text-ivory flex items-center justify-center shrink-0">
                    <b.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-serif text-[17px] text-forest leading-tight">{t(`trust.${b.key}_label`)}</div>
                    <div className="text-[12px] text-clay mt-0.5 leading-snug">{t(`trust.${b.key}_sub`)}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section data-testid="problem-section" className="paper-texture">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
          <Reveal>
            <div className="editorial-divider mb-6">{t("problem.eyebrow")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] text-forest max-w-4xl">
              {t("problem.h2a")} <span className="italic text-terracotta">{t("problem.h2b")}</span>
            </h2>
            <p className="mt-6 max-w-2xl text-lg text-ink/75 leading-relaxed">{t("problem.lead")}</p>
          </Reveal>
          <StaggerChildren className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {problems.map((p) => (
              <motion.div variants={staggerItem} key={p.key} data-testid={`problem-card-${p.key}`}
                className="p-7 border border-dust bg-ivory/60 rounded-xl hover:-translate-y-1 transition-transform duration-500">
                <div className="w-11 h-11 rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center mb-5">
                  <p.icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-xl text-forest">{t(`problem.${p.key}_title`)}</h3>
                <p className="mt-2 text-[14px] text-ink/70 leading-relaxed">{t(`problem.${p.key}_text`)}</p>
              </motion.div>
            ))}
          </StaggerChildren>
          <Reveal delay={0.2}>
            <div className="mt-20 p-8 lg:p-12 rounded-2xl bg-forest text-ivory flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div>
                <div className="text-[12px] tracking-[0.3em] uppercase text-mustard mb-2">{t("problem.answer_eyebrow")}</div>
                <h3 className="font-serif text-3xl lg:text-5xl leading-[1.08] max-w-3xl">{t("problem.answer")}</h3>
              </div>
              <Link to={localePath("/about")} data-testid="problem-learn-more-btn" className="btn-pill bg-mustard text-forest hover:bg-mustard/90 whitespace-nowrap">
                {t("problem.learn_more")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="how-it-works-section" className="bg-forest text-ivory">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
          <Reveal>
            <div className="editorial-divider mb-6" style={{ color: "#E3A839" }}>{t("how.eyebrow")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] max-w-4xl">
              {t("how.h2a")} <span className="italic text-mustard">{t("how.h2b")}</span>
            </h2>
          </Reveal>
          <StaggerChildren className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {steps.map((s, i) => (
              <motion.div key={s.n} variants={staggerItem} data-testid={`how-step-${i+1}`} className="relative">
                <div className="p-8 lg:p-10 rounded-xl bg-ivory/5 border border-ivory/10 hover:bg-ivory/10 transition-colors duration-500 h-full">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-full bg-mustard text-forest flex items-center justify-center">
                      <s.icon className="w-6 h-6" />
                    </div>
                    <div className="font-serif text-5xl text-mustard/70">{s.n}</div>
                  </div>
                  <h3 className="font-serif text-2xl lg:text-3xl">{t(`how.${s.key}_title`)}</h3>
                  <p className="mt-4 text-ivory/75 leading-relaxed">{t(`how.${s.key}_text`)}</p>
                </div>
                {i < 2 && <ArrowRight className="hidden md:block absolute top-1/2 -right-5 w-6 h-6 text-mustard/60 z-10" />}
              </motion.div>
            ))}
          </StaggerChildren>
          <Reveal delay={0.3}>
            <div className="mt-14 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <a href="tel:+919938056120" data-testid="how-call-btn" className="btn-pill bg-mustard text-forest hover:bg-mustard/90">
                <Phone className="w-4 h-4" /> {t("how.cta_call")}
              </a>
              <p className="text-ivory/65 text-[14px]">{t("how.note")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ABOUT SHORT */}
      <section data-testid="about-short-section" className="paper-texture">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <Reveal className="lg:col-span-5 relative">
              <div className="relative">
                <img src="/images/gallery/lsp-2.jpg" alt="LSP farmer training" className="w-full h-[420px] object-cover rounded-xl" loading="lazy" />
                <img src="/images/gallery/lsp-3.jpg" alt="" className="absolute -bottom-10 -right-6 w-44 h-56 object-cover rounded-xl ring-4 ring-ivory hidden sm:block" loading="lazy" />
                <div className="absolute -top-4 -left-4 px-4 py-2 rounded-full bg-mustard text-forest text-[11px] tracking-[0.3em] uppercase font-medium">Est · Odisha</div>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-7">
              <div className="editorial-divider mb-6">{t("about_short.eyebrow")}</div>
              <h2 className="font-serif text-4xl lg:text-6xl leading-[1.02] text-forest">
                {t("about_short.h2a")} <br /> <span className="italic text-terracotta">{t("about_short.h2b")}</span>
              </h2>
              <p className="mt-8 text-ink/80 text-lg leading-relaxed max-w-xl">{t("about_short.p1")}</p>
              <p className="mt-4 text-ink/70 leading-relaxed max-w-xl">{t("about_short.p2")}</p>
              <Link to={localePath("/about")} data-testid="about-readmore-btn" className="btn-pill btn-ghost mt-8">
                {t("common.our_full_story")} <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-section" className="kraft-texture">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <Reveal>
              <div className="editorial-divider mb-5">{t("services_home.eyebrow")}</div>
              <h2 className="font-serif text-4xl lg:text-6xl text-forest max-w-xl leading-[1.04]">
                {t("services_home.h2a")} <span className="italic text-terracotta">{t("services_home.h2b")}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}><p className="max-w-sm text-ink/70 leading-relaxed">{t("services_home.lead")}</p></Reveal>
          </div>
          <StaggerChildren className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <motion.div variants={staggerItem} key={s.key} data-testid={`service-card-${s.key}`}
                className="group relative rounded-xl overflow-hidden bg-ivory border border-dust hover:-translate-y-1 transition-all duration-500">
                <div className="relative h-56 overflow-hidden">
                  <img src={s.img} alt={t(`services_home.${s.key}_title`)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute top-5 left-5 w-11 h-11 rounded-full bg-mustard text-forest flex items-center justify-center">
                    <s.icon className="w-5 h-5" />
                  </div>
                </div>
                <div className="p-7">
                  <h3 className="font-serif text-2xl text-forest">{t(`services_home.${s.key}_title`)}</h3>
                  <p className="mt-3 text-ink/70 leading-relaxed">{t(`services_home.${s.key}_text`)}</p>
                </div>
              </motion.div>
            ))}
          </StaggerChildren>
          <div className="mt-14">
            <Link to={localePath("/services")} data-testid="services-viewall-btn" className="btn-pill btn-forest">
              {t("common.view_all_programs")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section data-testid="impact-section" className="bg-ivory">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
          <Reveal>
            <div className="editorial-divider mb-6">{t("impact.eyebrow")}</div>
            <h2 className="font-serif text-4xl lg:text-6xl text-forest leading-[1.04] max-w-3xl">
              {t("impact.h2a")} <span className="italic text-terracotta">{t("impact.h2b")}</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {impact.map((s, i) => (
              <Reveal key={s.key} delay={i * 0.08}>
                <div className="border-l-2 border-terracotta pl-5">
                  <div className="font-serif text-5xl lg:text-7xl text-forest leading-none">
                    <CountUp end={s.n} duration={2.4} enableScrollSpy scrollSpyOnce />{s.suffix}
                  </div>
                  <div className="mt-3 text-[13px] tracking-[0.2em] uppercase text-clay">{t(`impact.${s.key}`)}</div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-5"><h3 className="font-serif text-3xl text-forest">{t("impact.where_title")}</h3></div>
              <ul className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-3">
                {locations.map((loc) => (
                  <li key={loc} className="flex items-center gap-2 px-4 py-3 rounded-full border border-dust bg-kraft/40">
                    <MapPin className="w-4 h-4 text-terracotta" />
                    <span className="text-[14px] text-forest">{loc}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="bg-kraft/60 py-24 lg:py-28 border-y border-dust">
        <Reveal>
          <div className="text-center mb-12 px-5">
            <div className="editorial-divider max-w-sm mx-auto mb-4">{t("common.voices")}</div>
            <h2 className="font-serif text-3xl lg:text-5xl text-forest max-w-3xl mx-auto">
              {t("testimonials_home.h2a")} <span className="italic text-terracotta">{t("testimonials_home.h2b")}</span>
            </h2>
          </div>
        </Reveal>
        <div className="marquee-fade">
          <Marquee gradient={false} speed={40} pauseOnHover>
            {testimonials.concat(testimonials).map((tt, i) => (
              <div key={i} className="mx-4 w-[340px] sm:w-[420px] border border-dust bg-ivory rounded-xl p-7 min-h-[180px]">
                <div className="flex gap-1 mb-3 text-mustard">
                  {[...Array(5)].map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-current" />)}
                </div>
                <p className="font-serif text-lg text-forest leading-snug">&ldquo;{tt.q}&rdquo;</p>
                <div className="mt-4 text-[12px] tracking-[0.2em] uppercase text-clay">— {tt.by}</div>
              </div>
            ))}
          </Marquee>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="bg-ivory">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <HelpCircle className="w-6 h-6 text-terracotta" />
              <div className="editorial-divider flex-1">{t("faq_home.eyebrow")}</div>
            </div>
            <h2 className="font-serif text-4xl lg:text-6xl text-forest leading-[1.04] max-w-3xl">
              {t("faq_home.h2a")} <span className="italic text-terracotta">{t("faq_home.h2b")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-12">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-dust border-b">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="font-serif text-lg lg:text-2xl text-forest text-left hover:text-terracotta hover:no-underline py-6">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-ink/75 leading-relaxed text-[16px] pb-6 max-w-3xl">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="final-cta-section" className="relative isolate bg-forest">
        <div className="absolute inset-0 z-0">
          <img src="/images/gallery/lsp-12.jpg" alt="" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-black/55 md:bg-forest/85" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-28 lg:py-40 text-white">
          <Reveal>
            <h2 className="font-serif text-5xl lg:text-8xl leading-[0.95] max-w-4xl">
              {t("final_cta.h2a")} <br /> <span className="italic text-mustard">{t("final_cta.h2b")}</span>
            </h2>
            <p className="mt-8 max-w-xl text-white/85 text-lg leading-relaxed">{t("final_cta.lead")}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="tel:+919938056120" data-testid="cta-call-btn" className="btn-pill bg-mustard text-forest hover:bg-mustard/90">
                <Phone className="w-4 h-4" /> {t("final_cta.cta_call")}
              </a>
              <Link to={localePath("/contact")} data-testid="cta-contact-btn" className="btn-pill btn-terra">
                {t("final_cta.cta_msg")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to={localePath("/training")} data-testid="cta-training-btn" className="btn-pill bg-white/10 backdrop-blur-md text-white border border-white/30 hover:bg-white/20">
                {t("final_cta.cta_train")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
